/**
 * School resource officers: schools and lockdown drills. The drill is the Lockdown Drill Worksheet on the phone —
 * room by room, five yes/no checks, a stopwatch — and it prints like the paper worksheet.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { blankRoom, drillScore, LOCKDOWN_CHECKS, SCHOOL_LEVEL_LABEL, SCHOOL_LEVELS, stopwatchText, type LockdownRoom } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, PeoplePicker, when } from './common.tsx';
import { Box, fmtD, fmtT, Head, PrintSheet } from './printsheet.tsx';

const pctTone = (p: number | null) => (p == null ? 'neutral' : p >= 95 ? 'ok' : p >= 80 ? 'warn' : 'bad');

export function SchoolsPage() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/schools'));
  const [adding, setAdding] = useState(false);
  const schools: any[] = r.data?.schools ?? [];
  const due = schools.filter((s) => !s.lastDrill || Date.now() - +new Date(s.lastDrill.drill_at) > 90 * 86_400_000).length;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Schools"
        title="Schools"
        sub="Each school, its school resource officers, and its lockdown drills."
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => setAdding(!adding)}>
              {adding ? 'Close' : 'Add a school'}
            </button>
          )
        }
      />
      {adding && <SchoolForm people={r.data.people} onDone={(id) => (id ? navigate(at(`/schools/${id}`)) : setAdding(false))} />}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !schools.length ? (
        <EmptyState icon="book" title="No schools yet">
          {r.data.canManage ? 'Add the schools your SROs cover.' : 'The SRO supervisor adds schools here.'}
        </EmptyState>
      ) : (
        <>
          <div className="ops-kpis">
            <KPI n={schools.length} label="Schools" />
            <KPI n={schools.reduce((n, s) => n + s.drillsThisYear, 0)} label="Lockdown drills this year" />
            <KPI n={due} label="No drill in 90 days" tone={due ? 'warn' : undefined} />
          </div>
          <div className="sch-grid">
            {schools.map((s) => (
              <button key={s.id} type="button" className={`card sch-card${s.mine ? ' mine' : ''}`} onClick={() => navigate(at(`/schools/${s.id}`))}>
                <div className="row between">
                  <strong className="sch-name">{s.name}</strong>
                  <span className="small muted">{SCHOOL_LEVEL_LABEL[s.level as keyof typeof SCHOOL_LEVEL_LABEL]}</span>
                </div>
                <div className="small">{s.sros.length ? `SRO: ${s.sros.map((p: any) => p.name).join(', ')}` : <span className="muted">No SRO assigned</span>}</div>
                <div className="sch-last">
                  {s.lastDrill ? (
                    <>
                      <span>Last drill {day(s.lastDrill.drill_at)}</span>
                      <span className="mono">{stopwatchText(s.lastDrill.stopwatch_seconds)}</span>
                      <Badge tone={pctTone(s.lastDrill.pct)}>{s.lastDrill.pct}%</Badge>
                      {s.lastDrill.roomsWithMisses > 0 && <span className="small">{s.lastDrill.roomsWithMisses} room{s.lastDrill.roomsWithMisses === 1 ? '' : 's'} missed something</span>}
                    </>
                  ) : (
                    <Badge tone="warn">No lockdown drill yet</Badge>
                  )}
                  {s.drafts > 0 && <Badge tone="info">Drill in progress</Badge>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SchoolForm({ school, people, onDone }: { school?: any; people: any[]; onDone: (id?: string) => void }) {
  const [f, setF] = useState({
    name: school?.name ?? '',
    level: school?.level ?? 'elementary',
    address: school?.address ?? '',
    principal: school?.principal ?? '',
    phone: school?.phone ?? '',
    students: school?.students ?? '',
    staff: school?.staff ?? '',
    rooms: (school?.rooms ?? []).join(', '),
    notes: school?.notes ?? '',
  });
  const [sros, setSros] = useState<string[]>(school?.sro_ids ?? []);
  const a = useAction();
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <Card title={school ? 'Change the school' : 'Add a school'}>
      <form
        className="stack sm"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const body = {
              ...f,
              students: f.students === '' ? null : Number(f.students),
              staff: f.staff === '' ? null : Number(f.staff),
              rooms: String(f.rooms)
                .split(/[,\n]/)
                .map((x) => x.trim())
                .filter(Boolean),
              sroIds: sros,
            };
            if (school) {
              await api.post(`/api/schools/${school.id}`, body);
              onDone();
            } else onDone((await api.post<{ id: string }>('/api/schools', body)).id);
          });
        }}
      >
        <div className="grid-3">
          <label>
            School name
            <input value={f.name} onChange={set('name')} required />
          </label>
          <label>
            Level
            <select value={f.level} onChange={set('level')}>
              {SCHOOL_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {SCHOOL_LEVEL_LABEL[l]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Principal
            <input value={f.principal} onChange={set('principal')} />
          </label>
          <label>
            Address
            <input value={f.address} onChange={set('address')} />
          </label>
          <label>
            Office phone
            <input value={f.phone} onChange={set('phone')} inputMode="tel" />
          </label>
          <span className="grid-2">
            <label>
              Students
              <input value={f.students} onChange={set('students')} inputMode="numeric" />
            </label>
            <label>
              Staff
              <input value={f.staff} onChange={set('staff')} inputMode="numeric" />
            </label>
          </span>
        </div>
        <label>
          Rooms (separate with commas — these fill in each drill’s worksheet)
          <textarea rows={2} value={f.rooms} onChange={set('rooms')} placeholder="101, 102, 103, Library, Gym" />
        </label>
        <PeoplePicker people={people.map((p) => ({ id: p.id, name: p.name, title: p.title }) as any)} value={sros} onChange={setSros} label="School resource officer(s)" />
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            {school ? 'Save' : 'Add the school'}
          </button>
          <button type="button" className="btn ghost" onClick={() => onDone()}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

export function SchoolDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/schools/${id}`), [id]);
  const ppl = useLoad(() => api.get<any>('/api/schools'));
  const [edit, setEdit] = useState(false);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { school: s, drills, repeatRooms, canManage } = r.data;
  const draft = drills.find((d: any) => d.status === 'draft');
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Schools"
        title={s.name}
        sub={[SCHOOL_LEVEL_LABEL[s.level as keyof typeof SCHOOL_LEVEL_LABEL], s.address, s.principal && `Principal ${s.principal}`, s.phone].filter(Boolean).join(' · ')}
        actions={
          canManage && (
            <div className="row gap-sm">
              <button className="btn" onClick={() => setEdit(!edit)}>
                {edit ? 'Close' : 'Change'}
              </button>
              {draft ? (
                <button className="btn primary" onClick={() => navigate(at(`/schools/drills/${draft.id}`))}>
                  Finish the drill in progress
                </button>
              ) : (
                <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => navigate(at(`/schools/drills/${(await api.post<{ id: string }>('/api/lockdown-drills', { schoolId: id })).id}`)))}>
                  Start a lockdown drill
                </button>
              )}
            </div>
          )
        }
      />
      <ErrorBox error={a.error} />
      {edit && ppl.data && <SchoolForm school={s} people={ppl.data.people} onDone={() => (setEdit(false), r.reload())} />}
      <div className="grid-2-cards">
        <Card title="School">
          <dl className="kv">
            <dt>SRO</dt>
            <dd>{s.sros.length ? s.sros.map((p: any) => p.name).join(', ') : '—'}</dd>
            <dt>Students</dt>
            <dd>{s.students ?? '—'}</dd>
            <dt>Staff</dt>
            <dd>{s.staff ?? '—'}</dd>
            <dt>Rooms</dt>
            <dd>{s.rooms.length ? `${s.rooms.length}: ${s.rooms.join(', ')}` : '—'}</dd>
          </dl>
        </Card>
        <Card title="Rooms to work on">
          {!repeatRooms.length ? (
            <p className="small muted">No room missed a check on the last three drills.</p>
          ) : (
            <ul className="plain stack xs">
              {repeatRooms.map((x: any) => (
                <li key={x.room}>
                  <strong>Room {x.room}</strong> — {x.checks.join(', ')}{' '}
                  {x.drills > 1 && <Badge tone="bad">{x.drills} drills in a row</Badge>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <Card title="Lockdown drills">
        {!drills.length ? (
          <p className="small muted">No drills yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Stopwatch</th>
                  <th>Rooms</th>
                  <th>Missed</th>
                  <th>Score</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {drills.map((d: any) => (
                  <tr key={d.id} className="clickable" onClick={() => navigate(at(`/schools/drills/${d.id}`))}>
                    <td>{when(d.drill_at)}</td>
                    <td className="mono">{stopwatchText(d.stopwatch_seconds) || '—'}</td>
                    <td>{d.rooms}</td>
                    <td>{d.misses.length ? d.misses.map((m: any) => `${m.room} ${m.check.toLowerCase()}`).join('; ') : 'None'}</td>
                    <td>{d.pct != null ? <Badge tone={pctTone(d.pct)}>{d.pct}%</Badge> : '—'}</td>
                    <td>{d.by_name}</td>
                    <td>{d.status === 'draft' ? <Badge tone="info">In progress</Badge> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── The worksheet ─────────────────────────────────────────────────────────
function Stopwatch({ seconds, onChange, disabled }: { seconds: number | null; onChange: (s: number | null) => void; disabled: boolean }) {
  const [start, setStart] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (start == null) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [start]);
  const running = start != null;
  const shown = running ? Math.floor((now - start) / 1000) : seconds;
  const [typed, setTyped] = useState('');
  return (
    <div className="ld-watch">
      <div className="ld-time mono" aria-live="off" aria-label="Stopwatch time">
        {stopwatchText(shown ?? 0)}
      </div>
      {!disabled && (
        <div className="row gap-sm wrap">
          {!running ? (
            <button type="button" className="btn primary" onClick={() => (setStart(Date.now() - (seconds ?? 0) * 1000), setNow(Date.now()))}>
              {seconds ? 'Resume' : 'Start'}
            </button>
          ) : (
            <button type="button" className="btn danger" onClick={() => (onChange(Math.floor((Date.now() - start!) / 1000)), setStart(null))}>
              Stop
            </button>
          )}
          {!running && seconds != null && (
            <button type="button" className="btn ghost" onClick={() => onChange(null)}>
              Reset
            </button>
          )}
          {!running && (
            <span className="row gap-xs">
              <input className="ld-type mono" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="m:ss" aria-label="Type the stopwatch time" />
              <button
                type="button"
                className="btn small"
                disabled={!/^\d{1,3}:\d{2}$/.test(typed.trim())}
                onClick={() => {
                  const [m, s] = typed.trim().split(':').map(Number);
                  onChange(m! * 60 + s!);
                  setTyped('');
                }}
              >
                Set
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function YesNo({ value, onChange, label, disabled }: { value: boolean | null; onChange: (v: boolean | null) => void; label: string; disabled: boolean }) {
  return (
    <div className="ld-yn" role="radiogroup" aria-label={label}>
      <button type="button" role="radio" aria-checked={value === true} className={value === true ? 'on yes' : ''} disabled={disabled} onClick={() => onChange(value === true ? null : true)}>
        Yes
      </button>
      <button type="button" role="radio" aria-checked={value === false} className={value === false ? 'on no' : ''} disabled={disabled} onClick={() => onChange(value === false ? null : false)}>
        No
      </button>
    </div>
  );
}

export function DrillPage({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/lockdown-drills/${id}`), [id]);
  const [d, setD] = useState<any>(null);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState<'saved' | 'saving' | 'error' | null>(null);
  const [newRoom, setNewRoom] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const fin = useAction();
  useEffect(() => {
    if (r.data) setD({ ...r.data.drill, drillAtLocal: toLocal(r.data.drill.drill_at) });
  }, [r.data]);
  const canEdit = !!r.data?.canEdit;
  const latest = useRef<any>(null);
  latest.current = d;
  const save = useCallback(async () => {
    const x = latest.current;
    if (!x) return;
    setSaved('saving');
    try {
      await api.post(`/api/lockdown-drills/${id}`, {
        drillAt: x.drillAtLocal ? new Date(x.drillAtLocal).toISOString() : undefined,
        team: x.team || null,
        stopwatchSeconds: x.stopwatch_seconds,
        students: x.students === '' || x.students == null ? null : Number(x.students),
        staff: x.staff === '' || x.staff == null ? null : Number(x.staff),
        rooms: x.rooms,
        notes: x.notes || null,
      });
      setSaved('saved');
      setDirty(false);
    } catch {
      setSaved('error');
    }
  }, [id]);
  useEffect(() => {
    if (!dirty || !canEdit) return;
    const t = setTimeout(save, 700);
    return () => clearTimeout(t);
  }, [d, dirty, canEdit, save]);
  if (!r.data || !d) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const s = r.data.school;
  const upd = (patch: any) => (setD({ ...d, ...patch }), setDirty(true));
  const setRoom = (i: number, patch: Partial<LockdownRoom>) => upd({ rooms: d.rooms.map((x: LockdownRoom, j: number) => (j === i ? { ...x, ...patch } : x)) });
  const score = drillScore(d.rooms);
  const left = d.rooms.filter((x: LockdownRoom) => LOCKDOWN_CHECKS.some((c) => x[c.key] === null)).length;
  const shown = d.rooms.map((x: LockdownRoom, i: number) => [x, i] as const).filter(([x]: readonly [LockdownRoom, number]) => !onlyOpen || LOCKDOWN_CHECKS.some((c) => x[c.key] === null));
  return (
    <div className="ops stack ld">
      <PageHeader
        crumb={
          <button type="button" className="btn link" onClick={() => navigate(at(`/schools/${s.id}`))}>
            {s.name}
          </button>
        }
        title="Lockdown drill"
        sub={d.status === 'done' ? `Finished ${when(d.completed_at)} by ${d.completed_by_name ?? d.by_name}` : 'Go room by room. It saves as you go.'}
        actions={
          <div className="row gap-sm">
            {canEdit && <span className="small muted">{saved === 'saving' ? 'Saving…' : saved === 'error' ? 'Not saved — check your signal' : saved === 'saved' ? 'Saved' : ''}</span>}
            <button className="btn" onClick={() => navigate(at(`/schools/drills/${id}/print`))}>
              Print worksheet
            </button>
          </div>
        }
      />
      {d.status === 'done' && (
        <Notice tone={score.misses.length ? 'warn' : 'ok'}>
          {score.rooms} rooms · {score.pct}% of checks passed ·{' '}
          {score.misses.length ? `missed: ${score.misses.map((m) => `room ${m.room} ${m.check.toLowerCase()}`).join('; ')}` : 'no misses'}
        </Notice>
      )}
      <Card title="The drill">
        <div className="ld-top">
          <div className="grid-2">
            <label>
              Date / time
              <input type="datetime-local" value={d.drillAtLocal} disabled={!canEdit} onChange={(e) => upd({ drillAtLocal: e.target.value })} />
            </label>
            <label>
              Team members
              <input value={d.team ?? ''} disabled={!canEdit} onChange={(e) => upd({ team: e.target.value })} placeholder="Who walked the drill" />
            </label>
            <label>
              Student population
              <input value={d.students ?? ''} disabled={!canEdit} inputMode="numeric" onChange={(e) => upd({ students: e.target.value.replace(/\D/g, '') })} />
            </label>
            <label>
              Staff count
              <input value={d.staff ?? ''} disabled={!canEdit} inputMode="numeric" onChange={(e) => upd({ staff: e.target.value.replace(/\D/g, '') })} />
            </label>
          </div>
          <div>
            <div className="small strong">Stopwatch time</div>
            <Stopwatch seconds={d.stopwatch_seconds} disabled={!canEdit} onChange={(v) => upd({ stopwatch_seconds: v })} />
          </div>
        </div>
      </Card>
      <div className="row between wrap gap">
        <div className="small">
          <strong>
            {d.rooms.length - left} of {d.rooms.length}
          </strong>{' '}
          rooms checked{score.roomsWithMisses ? ` · ${score.roomsWithMisses} with a “No”` : ''}
        </div>
        <label className="check small">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Show only rooms not done
        </label>
      </div>
      <div className="ld-rooms">
        {shown.map(([x, i]: readonly [LockdownRoom, number]) => {
          const done = LOCKDOWN_CHECKS.every((c) => x[c.key] !== null);
          const miss = LOCKDOWN_CHECKS.some((c) => x[c.key] === false);
          return (
            <section key={x.room} className={`card ld-room${done ? ' done' : ''}${miss ? ' miss' : ''}`} aria-label={`Room ${x.room}`}>
              <div className="row between">
                <h3>Room {x.room}</h3>
                {canEdit && (
                  <div className="row gap-xs">
                    <button type="button" className="btn small" onClick={() => setRoom(i, { locks: true, lights: true, outOfSight: true, doorKnock: true, optionsUsed: true })}>
                      All yes
                    </button>
                    <button type="button" className="btn small ghost" aria-label={`Take room ${x.room} off this drill`} onClick={() => upd({ rooms: d.rooms.filter((_: any, j: number) => j !== i) })}>
                      ✕
                    </button>
                  </div>
                )}
              </div>
              {LOCKDOWN_CHECKS.map((c) => (
                <div key={c.key} className="ld-check">
                  <span>
                    <strong>{c.label}</strong>
                    <span className="small muted"> {c.ask}</span>
                  </span>
                  <YesNo value={x[c.key]} disabled={!canEdit} label={`Room ${x.room} ${c.label}`} onChange={(v) => setRoom(i, { [c.key]: v } as any)} />
                </div>
              ))}
              {(canEdit || x.note) && <input className="ld-note" value={x.note ?? ''} disabled={!canEdit} onChange={(e) => setRoom(i, { note: e.target.value })} placeholder="Note (what was wrong, who was told)" aria-label={`Room ${x.room} note`} />}
            </section>
          );
        })}
      </div>
      {canEdit && (
        <form
          className="row gap-sm"
          onSubmit={(e) => {
            e.preventDefault();
            const n = newRoom.trim();
            if (!n || d.rooms.some((x: LockdownRoom) => x.room.toLowerCase() === n.toLowerCase())) return;
            upd({ rooms: [...d.rooms, blankRoom(n)] });
            setNewRoom('');
          }}
        >
          <input value={newRoom} onChange={(e) => setNewRoom(e.target.value)} placeholder="Room # not on the list" aria-label="Add a room" />
          <button className="btn">Add room</button>
        </form>
      )}
      <Card title="Notes">
        <textarea rows={3} value={d.notes ?? ''} disabled={!canEdit} onChange={(e) => upd({ notes: e.target.value })} placeholder="How it went, what to fix, who you told" aria-label="Drill notes" />
      </Card>
      <ErrorBox error={fin.error} />
      {canEdit && (
        <div className="row gap wrap">
          <button
            className="btn primary"
            disabled={fin.busy}
            onClick={() =>
              fin.run(async () => {
                await save();
                await api.post(`/api/lockdown-drills/${id}/finish`);
                r.reload();
              })
            }
          >
            Finish the drill
          </button>
          <button
            className="btn ghost"
            disabled={fin.busy}
            onClick={() => {
              if (confirm('Throw away this drill?')) fin.run(async () => (await api.post(`/api/lockdown-drills/${id}/remove`), navigate(at(`/schools/${s.id}`), true)));
            }}
          >
            Throw away
          </button>
        </div>
      )}
    </div>
  );
}

const toLocal = (iso: string) => {
  const t = new Date(iso);
  t.setMinutes(t.getMinutes() - t.getTimezoneOffset());
  return t.toISOString().slice(0, 16);
};

/** The worksheet on paper: seven rooms across, the five checks down, Yes/No boxes, notes under each block. */
export function DrillPrint({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/lockdown-drills/${id}`), [id]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { drill: d, school: s, agency } = r.data;
  const rooms: LockdownRoom[] = d.rooms;
  const blocks: LockdownRoom[][] = [];
  for (let i = 0; i < Math.max(rooms.length, 1); i += 7) blocks.push(rooms.slice(i, i + 7));
  const sc = drillScore(rooms);
  return (
    <PrintSheet label="Lockdown drill worksheet">
      <Head agency={agency} title="Lockdown Drill Worksheet" sub={d.status === 'draft' ? 'IN PROGRESS — not finished' : `${sc.pct ?? 0}% of checks passed · ${sc.roomsWithMisses} room(s) missed a check`} />
      <div className="pf-grid c6">
        <Box l="School" v={s.name} span={4} />
        <Box l="Date / time" v={`${fmtD(d.drill_at)} ${fmtT(d.drill_at)}`} span={2} />
        <Box l="Team members" v={d.team} span={6} />
        <Box l="Stopwatch time" v={stopwatchText(d.stopwatch_seconds)} span={2} mono big />
        <Box l="Student population" v={d.students} span={2} />
        <Box l="Staff count" v={d.staff} span={2} />
      </div>
      {blocks.map((b, bi) => (
        <div key={bi} className="pf-keep">
          <table className="pf-ld">
            <thead>
              <tr>
                <th>Room #</th>
                {Array.from({ length: 7 }, (_, i) => (
                  <th key={i}>{b[i]?.room ?? ''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LOCKDOWN_CHECKS.map((c) => (
                <tr key={c.key}>
                  <th>{c.label}</th>
                  {Array.from({ length: 7 }, (_, i) => {
                    const v = b[i]?.[c.key];
                    return (
                      <td key={i} className={v === false ? 'no' : ''}>
                        {b[i] ? (
                          <>
                            <span className={`pf-cb${v === true ? ' on' : ''}`}>{v === true ? '✕' : ''}</span>Yes <span className={`pf-cb${v === false ? ' on' : ''}`}>{v === false ? '✕' : ''}</span>No
                          </>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pf-notes">
            <span className="pf-l">Notes</span>
            <div>
              {b
                .filter((x) => x.note)
                .map((x) => (
                  <div key={x.room}>
                    Room {x.room}: {x.note}
                  </div>
                ))}
              {bi === blocks.length - 1 && d.notes && <div>{d.notes}</div>}
            </div>
          </div>
        </div>
      ))}
      <p className="pf-foot">
        {d.by_name}
        {d.completed_at ? ` · finished ${fmtD(d.completed_at)} ${fmtT(d.completed_at)}` : ''} · printed {fmtD(new Date().toISOString())}
      </p>
    </PrintSheet>
  );
}
